// Runtime view of every platform cap exposed by GET /api/v1/limits.
//
// Initialized synchronously from the constants baked into @mg/shared at
// build time — every component renders with sensible defaults before any
// network round-trip, and a deployed Console keeps working even if the
// limits endpoint is briefly unavailable.
//
// `platformLimits.refresh()` pulls authoritative values from the control
// plane. Bumping a cap server-side propagates to clients on the next refresh
// without a rebuild — useful when ops wants to relax a cap mid-flight.
//
// Components read fields directly off the singleton (e.g.
// `platformLimits.deploy.max_file_bytes`). Svelte 5 picks up reactivity
// through the class's $state.raw fields, so no derived/store ceremony is
// needed at call sites. Class pattern per Svelte's recommendation —
// preferred over module-level $state + getter pairs.

import {
  DEPLOY_LIMITS as DEPLOY_LIMITS_BUILD_TIME,
  type DeployLimits,
} from '@mg/shared/deploy/validation';
import {
  PLATFORM_RUNTIME_LIMITS as RUNTIME_LIMITS_BUILD_TIME,
  type PlatformRuntimeLimits,
} from '@mg/shared/limits';
import { apiClient } from '$lib/api/client';

type LlmLimits = PlatformRuntimeLimits['llm'];
type BlobLimits = PlatformRuntimeLimits['blob'];

const REFRESH_TTL_MS = 60_000;

class PlatformLimits {
  // Public reactive state. Components read these directly; Svelte 5 tracks
  // the access. $state.raw because we only ever reassign whole objects
  // (never mutate individual fields) — skips the deep-proxy overhead.
  deploy = $state.raw<DeployLimits>(DEPLOY_LIMITS_BUILD_TIME);
  llm = $state.raw<LlmLimits>(RUNTIME_LIMITS_BUILD_TIME.llm);
  blob = $state.raw<BlobLimits>(RUNTIME_LIMITS_BUILD_TIME.blob);

  // Dedup bookkeeping. NOT $state — these are internal coordination, not
  // reactive deps. Promoting them to $state would re-fire any caller that
  // reads them through an effect, which is the bug that triggered this
  // refactor in the first place.
  #inFlight: Promise<void> | null = null;
  #lastSuccessMs = 0;

  /**
   * Pull authoritative limits from the control plane. Best-effort.
   *
   * Idempotent within the TTL window. Concurrent callers share the in-flight
   * promise. Failure path leaves #lastSuccessMs at 0 so the next call
   * retries immediately (build-time defaults stay in effect meanwhile).
   * Pass `force: true` to bypass the TTL.
   */
  refresh(opts: { force?: boolean } = {}): Promise<void> {
    if (this.#inFlight) return this.#inFlight;
    if (!opts.force && Date.now() - this.#lastSuccessMs < REFRESH_TTL_MS) {
      return Promise.resolve();
    }
    this.#inFlight = (async () => {
      try {
        const res = await apiClient.getLimits();
        this.deploy = res.deploy;
        this.llm = res.llm;
        this.blob = res.blob;
        this.#lastSuccessMs = Date.now();
      } catch (err) {
        // Build-time defaults are still in place; surface only to the dev
        // console so a transient CP outage doesn't block app rendering.
        console.warn('[limits] refresh failed; using build-time defaults:', err);
      } finally {
        this.#inFlight = null;
      }
    })();
    return this.#inFlight;
  }
}

export const platformLimits = new PlatformLimits();
